/**
 * RHViewTokens - view-specific configuration & data (table column weights, per-view
 * dimensions, label/translation maps). NOT design tokens - they reference the design
 * system in ./RHDesignTokens but belong to their screens, not the design vocabulary.
 */

import { RHButtons, RHDialogs, RHFont, RHRadius, RHSpacing } from "./RHDesignTokens"


export const RHResponsiveTextTokens = {

    topBarLabels: {
        offererDashboard: {
            regular: "Töölaud",
            compact: "Töölaud"
        },
        publishedProcurements: {
            regular: "Hanked",
            compact: "Hanked"
        },
        ongoingProcurements: {
            regular: "Töös hanked",
            compact: "Töös"
        },
        incomingOffers: {
            regular: "Pakkumused",
            compact: "Pakkumused"
        },
        outgoingOffers: {
            regular: "Esitatud pakkumused",
            compact: "Esitatud pakkumused"
        },
        statistics: {
            regular: "Avalik statistika",
            compact: "Avalik stat."
        }
    },
    tableHeaderLabels: {
        referenceNumber: {
            regular: "Viitenumber",
            compact: "Viitenr"
        },
        registeredTenderers: {
            regular: "Registreerunud pakkujaid",
            compact: "Registreerunuid"
        },
        estimatedCost: {
            regular: "Eeldatav maksumus (KM-ta)",
            compact: "Maksumus (KM-ta)"
        },
        procurementEstimatedCost: {
            regular: "Hanke eeldatav maksumus (KM-ta)",
            compact: "Maksumus (KM-ta)"
        },
        publicationTime: {
            regular: "Avaldamise aeg",
            compact: "Avaldatud"
        },
        // "Avaldatud" header for the procurer dashboard "Avaldatud" tab
        // (MyOngoingProcurementsViewController). Kept separate from `publicationTime`
        // so the offerer's "Avaldatud hanked" view keeps showing "Avaldamise aeg".
        published: {
            regular: "Avaldatud",
            compact: "Avaldatud"
        },
        offerCount: {
            regular: "Pakkumusi",
            compact: "Pakkumusi"
        }
    }

}


export const RHTableColumnMinimumWidths = {

    procurementTitle: 420,
    procurementStatus: 190,
    tableAction: RHButtons.tableActionWidth

}


export const RHLoginViewTokens = {

    introFontWeight: RHFont.weightRegular,
    introLineHeight: "1.8",
    authContentMaxWidth: 650,
    authButtonHeight: 40,
    authButtonTextSafetyBuffer: 24,
    welcomeHeadingFontSize: "2rem",
    welcomeHeadingFontWeight: RHFont.weightSemibold,
    oauthButtonLabelFontSize: "14px",
    oauthButtonLabelSafetyBuffer: 6

}


export const RHForgotPasswordViewTokens = {

    titleFontWeight: RHFont.weightMedium,
    fieldMinHeight: 40,
    submitButtonHeight: 40,
    submitButtonMinWidth: 220,
    submitButtonMaxWidth: 320

}


export const RHOffererSubmissionOutcomeTokens = {

    defaultFontWeight: RHFont.weightRegular,
    outcomeFontWeight: RHFont.weightSemibold

}


export const RHFilterPanelTokens = {

    chevronCollapsedGlyph: "▾",
    panelBorderRadius: `${RHRadius.large}px`,
    labelWidth: 190,
    rangeFieldWidth: 150,
    minimumRangeFieldWidth: 120,
    rangeSeparatorWidth: 14,
    actionButtonWidth: RHSpacing.xxLarge * 2,
    actionButtonHeight: RHButtons.tableActionHeight

}


export const RHFeedbackViewTokens = {

    dialogMaxWidth: 800,
    dialogWidthViewportRatio: 0.94,
    labelFontWeight: RHFont.weightMedium,
    ratingRowHeight: RHSpacing.xLarge + RHSpacing.small,
    ratingStarButtonSize: RHSpacing.xLarge + RHSpacing.medium

}


export const RHProcurerMainViewTokens = {

    actionButtonWidth: 200,
    actionButtonHeight: 48

}


export const RHSegmentedTabBarViewTokens = {

    unselectedVariant: "light" as const

}


export const RHProcurementSearchViewTokens = {

    cpvSearchButtonLabel: "Otsi CPV koodi järgi",
    cpvSearchButtonHeight: 28,
    columnMinimumWidths: {
        status: RHTableColumnMinimumWidths.procurementStatus,
        action: RHTableColumnMinimumWidths.tableAction
    }

}


export const RHCopyProcurementDialogViewTokens = {

    // Copy dialog needs a real table but should stay a focused modal, not a full-page replacement.
    dialogMaxWidth: 1440,
    dialogWidthViewportRatio: 0.94,
    dialogViewportMargin: RHSpacing.xLarge,
    dialogBackgroundColor: RHDialogs.dialogSurfaceBackgroundColor,
    dialogBorderRadius: RHDialogs.dialogSurfaceBorderRadius,
    dialogBoxShadow: RHDialogs.dialogSurfaceBoxShadow,
    contentInset: RHDialogs.dialogContentInset,
    get titleTextViewType() { return RHDialogs.dialogTitleTextViewType },
    get titleTextColor() { return RHDialogs.dialogTitleTextColor },
    headerHeight: RHSpacing.xLarge + RHSpacing.medium,
    titleToFilterGap: RHSpacing.medium,
    filterToTableGap: RHSpacing.medium,
    tableHeight: 520,
    tableMinHeight: 320,
    tableMaxViewportRatio: 0.62,
    footerTopGap: RHSpacing.large,
    footerButtonHeight: RHButtons.compactPrimaryHeight,
    footerButtonWidth: 160,
    footerButtonVariant: "softDanger" as const,
    get footerButtonContentPadding() { return RHButtons.compactPrimaryContentPadding },
    get footerButtonFontSize() { return RHButtons.compactPrimaryFontSize },
    selectButtonWidth: 118,
    selectButtonHeight: 32,
    selectButtonVariant: "success" as const,
    // [Reference number, Name, Company, Type, Status, Date, Deadline, Value, Selection].
    // The name is the primary selection cue; Company follows the established
    // procurement-table rhythm while compact categorical/date columns yield space.
    columnWeights: [1.0, 3.2, 1.3, 1.0, 1.2, 1.0, 1.0, 1.0, 0.9],
    columnMinimumWidths: {
        title: 300,
        type: RHSpacing.xLarge * 3 + RHSpacing.small,
        status: RHTableColumnMinimumWidths.procurementStatus,
        selection: 118
    },
    tableHeaderHeight: 56,
    headerContentPadding: 2,
    headerLineHeight: RHFont.lineHeightHeading,  // old: "1.15" — clipped g/j descenders
    headerFontSize: "13px",
    headerLetterSpacing: "0"

}


export const RHProcurementDetailsViewTokens = {

    contentMaxWidth: 1100,
    qaContentMaxWidth: 1375,
    closureDialog: {
        maxWidth: RHDialogs.askQuestionDialogMaxWidth,
        widthViewportRatio: 0.94
    },
    detailSplitLayoutBreakpoint: 620,
    detailLabelColumnWidth: 300,
    detailRowMinHeight: 56,
    detailRowGap: 0,
    detailHeaderFontWeight: RHFont.weightSemibold,
    detailHeaderLetterSpacing: "0.01em",
    detailValueFontWeight: RHFont.weightRegular,
    cpvHangingIndent: 12,
    editDeadlineButtonWidth: 90,
    editDeadlineButtonHeight: 32,
    tabButtonWidth: 210,
    tabButtonSelectedVariant: "primary" as const,
    tabButtonUnselectedVariant: "light" as const,
    promptActionButtonVariant: "primary" as const,
    promptActionButtonMinimumWidth: 240,
    promptActionButtonMaximumWidth: 360,
    askQuestionButtonVariant: "success" as const

}


export const RHOfferDetailDialogViewTokens = {

    sectionTitleFontWeight: RHFont.weightRegular,
    evaluationCardBorderWidth: "2px",
    requiredDocumentLabelHeight: 20,
    footerButtonWidth: 176

}


export const RHOffersEvaluationViewTokens = {

    tendererOverviewColumnWeights: [2, 1, 1.45, 1.25, 1.45],
    offerersMultilineHeaderLineHeight: "1.15",
    partOffersTableColumnWeights: [1.8, 0.2, 0.95, 1.25, 0.62, 0.42],
    partOffersOffererColumnMinimumWidth: 240,
    partOffersWinnerColumnMinimumWidth: 96,
    partOffersTableMaximumHeight: 520,
    partOffersResultActionWidth: RHTableColumnMinimumWidths.tableAction - RHSpacing.medium,
    partOffersMultilineHeaderLineHeight: "1.15",
    partOffersMeetsRequirementsTogglePillSegmentHeight: 28,
    finalizeButtonTitle: "Lõpeta hindamine ja teavita pakkujaid",
    finalizeButtonVariant: "success" as const

}


export const RHProcurementWorkflowTitleTokens = {

    contentTopGap: 6.6

}


export const RHMyOngoingProcurementsViewTokens = {

    // Leading 0.5 = the favorite-star column (roomy, so the star sits fully visible).
    columnWeights: [0.5, 1.2, 3.0, 1.2, 1.2, 1.15, 1.05, 0.85, 1.9, 1],
    // Only spliced into columnWeights (after "Nimetus") when the "Ettevõte"
    // column is shown — multi-company users only. See _currentColumnWeights.
    companyColumnWeight: 1.3,
    columnMinimumWidths: {
        title: RHTableColumnMinimumWidths.procurementTitle,
        status: RHTableColumnMinimumWidths.procurementStatus,
        action: RHTableColumnMinimumWidths.tableAction
    },
    multilineHeaderLineHeight: RHFont.lineHeightHeading  // old: "1.15" — 1.15em line box < Source Sans Pro content area (~1.26em); inner textElementView overflow:hidden clipped g/j descenders

}


export const RHDraftProcurementsViewTokens = {

    // [Loomise aeg, Tüüp, Pealkiri, Ava/Muuda] — the table has 4 VISIBLE columns, so
    // exactly 4 weights. A longer array mis-assigns weights per column (the old
    // 6-entry array gave the "Ava" button column 2.6/5 of the width and
    // produced a blank leading strip before the centred date column).
    // columnWeights: [1, 1.4, 2.6, 0.85, 1.1, 0.9],  // old: stale 6-column weights from the removed "Sinu hanked" landing table
    // columnWeights: [0.7, 3.3, 1],  // old: 3-column weights from before the "Tüüp" column
    // columnWeights: [0.7, 0.9, 2.4, 1],  // old: single-button action column, before the template "Muuda malli" second button
    columnWeights: [0.75, 0.75, 2.1, 1.0, 1.55],
    // Only spliced into columnWeights (after "Pealkiri") when the "Ettevõte"
    // column is shown — multi-company users only.
    companyColumnWeight: 1.3,
    contentMaxWidth: 1100,
    tableHeight: 800,
    headerActionWidth: 180,
    headerAction: {
        variant: "success" as const,
        height: RHSpacing.xLarge,
        contentPadding: RHSpacing.small,
        fontSize: RHButtons.compactFontSize,
        fontWeight: RHFont.weightMedium,
        gap: RHSpacing.small,
        widthSafetyBuffer: RHSpacing.small
    },
    draftRowActions: {
        width: 96,
        height: 30,
        gap: RHSpacing.tiny,
        contentPadding: RHSpacing.tiny,
        continueVariant: RHButtons.tableActionVariant,
        deleteVariant: "softDanger" as const
    },
    actionsMenu: {
        width: 240,
        rowHeight: RHButtons.compactHeight,
        gap: RHSpacing.tiny,
        anchorGap: RHSpacing.tiny,
        viewportInset: RHSpacing.small,
        contentInset: RHSpacing.small,
        zIndex: 10020,
        backgroundColor: RHDialogs.dialogSurfaceBackgroundColor,
        borderRadius: RHDialogs.dialogSurfaceBorderRadius,
        boxShadow: RHDialogs.dialogSurfaceBoxShadow
    },
    multilineHeaderLineHeight: RHFont.lineHeightHeading  // old: "1.15" — 1.15em line box < Source Sans Pro content area (~1.26em); inner textElementView overflow:hidden clipped g/j descenders

}


export const RHPublishedProcurementsViewTokens = {

    // Leading 0.5 = the favorite-star column (roomy, so the star sits fully visible).
    columnWeights: [0.5, 1.3, 2.7, 1.8, 1.2, 1.1, 1.3, 1.1, 1.2, 1],
    columnMinimumWidths: {
        status: RHTableColumnMinimumWidths.procurementStatus,
        action: RHTableColumnMinimumWidths.tableAction
    },
    multilineHeaderLineHeight: RHFont.lineHeightHeading  // old: "1.15" — 1.15em line box < Source Sans Pro content area (~1.26em); inner textElementView overflow:hidden clipped g/j descenders

}


export const RHOffererDashboardProcurementsViewTokens = {

    // Leading 0.5 = the favorite-star column; 1.1 = the "why listed" exposure badge,
    // this table's distinguishing column over the plain published-procurements list.
    columnWeights: [0.5, 1.1, 1.3, 2.7, 1.8, 1.2, 1.3, 1.3, 1],
    // Spliced in after "Nimetus" when the "Ettevõte" column shows (multi-company users only).
    companyColumnWeight: 1.3,
    columnMinimumWidths: {
        status: RHTableColumnMinimumWidths.procurementStatus,
        action: RHTableColumnMinimumWidths.tableAction
    },
    multilineHeaderLineHeight: RHFont.lineHeightHeading

}


export const RHIncomingOffersViewTokens = {

    columnWeights: [1.3, 3, 2, 0.7, 1.1, 1.1, 1.2, 0.8],
    multilineHeaderLineHeight: RHFont.lineHeightHeading  // old: "1.15" — 1.15em line box < Source Sans Pro content area (~1.26em); inner textElementView overflow:hidden clipped g/j descenders

}


export const RHEventLogViewTokens = {

    tableLinkContentPadding: 0,
    notificationsColumnWeights: [1.05, 4],
    // Appended after "Teatis" when the "Ettevõte" column shows (multi-company users only).
    notificationsCompanyColumnWeight: 1.3,
    eventLogColumnWeights: [1.05, 3.95, 0.65],
    markAllButtonHeight: 28

}


export const RHMakeOfferViewTokens = {

    saveButtonTitle: "Salvesta",
    proceedButtonTitle: "Kinnita",
    confirmButtonVariant: "success" as const,
    actionButtonHeight: RHButtons.compactHeight + 4,
    actionButtonMinWidth: 240,
    actionButtonMaxWidth: 340,
    submittedOfferEditDialogWidth: 1000,
    offerItemPriceFieldWidth: 220,
    offerItemCurrencyLabelWidth: 24,
    deleteOfferButtonSize: 34

}


export const RHOutgoingOffersViewTokens = {

    columnWeights: [1.3, 3, 0.7, 1, 1.1, 1.1, 1.2, 0.8],
    multilineHeaderLineHeight: RHFont.lineHeightHeading  // old: "1.15" — 1.15em line box < Source Sans Pro content area (~1.26em); inner textElementView overflow:hidden clipped g/j descenders

}


export const RHContractsViewTokens = {

    columnWeights: [1.4, 2.4, 1.6, 0.9, 1.2, 1.2, 1.35, 1.1, 1, 1.1],
    editDialogContentWidth: 720,
    procurementDetailColumnWeights: {
        procurer: 2.5,
        contractor: 2.5,
        parts: 0.55,
        value: 0.8,
        decisionDate: 0.85,
        contractNumber: 0.7,
        contractDate: 0.95,
        status: 1.1
    },
    procurementDetailStatusActionWidth: RHButtons.tableActionWidth,
    multilineHeaderLineHeight: RHFont.lineHeightHeading  // old: "1.15" — 1.15em line box < Source Sans Pro content area (~1.26em); inner textElementView overflow:hidden clipped g/j descenders

}


export const RHProcurementPlanViewTokens = {

    columnWeights: [3, 1.3, 1.1, 1.3, 1.2],
    multilineHeaderLineHeight: RHFont.lineHeightHeading  // old: "1.15" — 1.15em line box < Source Sans Pro content area (~1.26em); inner textElementView overflow:hidden clipped g/j descenders

}


export const RHInvoicesViewTokens = {

    multilineHeaderLineHeight: RHFont.lineHeightHeading  // old: "1.15" — 1.15em line box < Source Sans Pro content area (~1.26em); inner textElementView overflow:hidden clipped g/j descenders

}


export const RHNewProcurementViewTokens = {

    contentMaxWidth: 990,
    pairedOptionButtonsMaxWidth: 260,
    publicationModeOptionsMaxWidth: 380,
    estimatedCostOptionButtonsMaxWidth: 160,
    copyPreviousProcurementButtonMaxWidth: 220,
    fieldLabelFontWeight: RHFont.weightSemibold,
    multipleOffersFieldHeight: 36,
    autoExtendRuleFieldHeight: 32,
    autoExtendRuleFieldWidth: 64,
    templateSourceActions: {
        height: RHButtons.excelExportButtonSize,
        iconButtonSize: RHButtons.excelExportButtonSize,
        iconSize: RHButtons.excelExportIconSize,
        contentPadding: RHSpacing.small,
        fontSize: RHFont.sizeSmall,
        gap: RHSpacing.small,
        textButtonMaxWidth: 210
    },
    templateLockControl: {
        size: RHButtons.dialogCloseSize - RHSpacing.small,
        iconSize: (RHButtons.dialogCloseSize - RHSpacing.small) * 0.5,
        gap: RHSpacing.small,
        contentPadding: RHButtons.dialogCloseContentPadding,
        padding: RHButtons.dialogClosePadding,
        borderRadius: RHButtons.dialogCloseBorderRadius
    }

}


export const RHCPVSummationViewTokens = {

    headerActionWidth: 148,
    headerActionHeight: RHButtons.compactHeight,
    headerStacksBelowWidth: 620,
    levelButtonMinimumHeight: RHButtons.compactHeight,
    levelButtonIndent: RHSpacing.medium,
    copyButtonWidth: 148,
    amountSummaryTwoColumnMinimumWidth: 620

}


export const RHValidationIssuesDialogViewTokens = {

    // CBDialogViewShower adds the shared dialog inset around structured alert content.
    preferredWidth:
        RHDialogs.confirmationQuestionDialogPreferredWidth - RHDialogs.confirmationDialogContentInset * 2,
    issueListMaxHeight: 320,
    issueListInset: RHSpacing.medium,
    titleToGuidanceGap: RHSpacing.tiny,
    guidanceToIssueListGap: RHSpacing.medium,
    issueRowGap: RHSpacing.small

}


export const RHCompanyViewTokens = {

    tabButtonWidthSafetyBuffer: RHSpacing.xLarge + RHSpacing.small,
    // The combined administration table has six columns and two fixed-width
    // action buttons. Keep it wider than form-oriented screens so the desktop
    // table does not need horizontal scrolling.
    administrationContentMaxWidth: 1280,
    // Matches the content width used by ProcurementDetailsViewController.
    tenderInterestContentMaxWidth: 1100

}


export const RHCompanyTenderInterestViewTokens = {

    dataViewHeights: {
        administratorCompany: 840,
        representativeCompanyDefaults: 480,
        representativePersonalExtras: 640
    },
    columnWeights: {
        cpvCode: 0.6,
        description: 2.4,
        action: 0
    },
    headerAction: {
        height: 28,
        contentPadding: RHSpacing.small,
        fontSize: RHFont.sizeXSmall,
        fontWeight: RHFont.weightMedium,
        gap: RHSpacing.small,
        widthSafetyBuffer: RHSpacing.small,
        minimumWidth: RHSpacing.xxLarge * 2
    }

}


export const RHCompanyAdministrationViewTokens = {

    contractingAuthorityTypeButtonsMaxWidth: 520,
    membershipRequestDialog: {
        // This dialog combines a four-column company table with role controls,
        // so it needs more desktop width than the focused table dialogs.
        maxWidth: 1440,
        widthViewportRatio: 0.94,
        backgroundColor: RHDialogs.dialogSurfaceBackgroundColor,
        borderRadius: RHDialogs.dialogSurfaceBorderRadius,
        boxShadow: RHDialogs.dialogSurfaceBoxShadow,
        footerButtonHeight: RHButtons.compactHeight,
        footerButtonGap: RHSpacing.medium,
        footerButtonContentPadding: RHButtons.compactContentPadding,
        footerButtonTextSafetyBuffer: RHSpacing.medium,
        footerButtonMinWidth: 160,
        footerButtonMaxWidth: 240,
        searchResultsHeight: 420
    },
    pendingWorkflowsTableHeight: 300,
    membersTableHeight: 360,
    affiliationColumnMinimumWidths: {
        name: 128,
        email: 160,
        role: 220,
        status: 112
    },
    employeeDialogSaveButtonWidth: RHSpacing.xxLarge * 3,
    // Two fixed-width action buttons ("Halda rolle" + "Eemalda") side by side,
    // each at the shared tableActionWidth (130) so they match "Vaata"; the
    // column must fit 130 + gap + 130 plus a little centring room.
    memberActionsColumnMinimumWidth: 290,
    identityCellLineHeight: "1.6",
    roleDisplayLabels: {
        administrator: "Peakasutaja",
        procurer: "Hankija ettevõtte esindaja",
        offerer: "Pakkuja ettevõtte esindaja",
        auditor: "Audiitor",
        consultant: "Konsultant (pärandroll)"
    } as Record<string, string>,  // backend role keys stay canonical; only UI display strings
    missingRoleDisplayLabel: "Ettevõtte roll puudub",
    roleFilterChipLabels: {
        all: "Kõik",
        administrator: "Peakasutaja",
        procurer: "Hankija esindaja",
        offerer: "Pakkuja esindaja"
    },
    lastAdministratorLockHoverText: "Ettevõttel peab olema vähemalt üks peakasutaja",
    soleAdministratorLeaveExplanation:
        "Ettevõttel peab olema vähemalt üks peakasutaja. " +
        "Kui soovid rollist loobuda, tuleb see esmalt üle anda.",
    procurerIneligibleExplanation:
        "Sellel ettevõttel pole riigihangete registris ühtki lõppenud hanget hankijana, " +
        "seetõttu ei saa talle hankija õigusi anda."

}


export const RHLogViewTokens = {

    tabButtonWidth: RHSpacing.xxLarge * 4

}


export const RHLogEventViewTokens = {

    dialogMaxWidth: 800,
    rowHeight: RHSpacing.xLarge + RHSpacing.medium,
    eventTypeLabels: {
        "user.registered": "Kasutaja registreeriti",
        "user.login": "Kasutaja logis sisse",
        "user.login_failed": "Sisselogimine ebaõnnestus",
        "user.password_changed": "Salasõna muudeti",
        "user.totp_secret_decryption_authorized": "Autentimisrakenduse saladus dekrüpteeriti",
        "user.totp_secret_decryption_denied": "Autentimisrakenduse saladuse dekrüpteerimisest keelduti",
        "user.session_invalidated": "Sessioon lõpetati",
        "user.deactivated": "Kasutaja deaktiveeriti",
        "company.registration_requested": "Ettevõtte registreerimist taotleti",
        "company.registration_confirmed": "Ettevõtte registreerimine kinnitati",
        "company.domain_verification_requested": "Domeeni kinnitamist taotleti",
        "company.domain_verified": "Domeen kinnitati",
        "company.user_invited": "Kasutaja kutsuti ettevõttesse",
        "company.join_request_created": "Ettevõttega liitumise taotlus esitati",
        "company.join_request_approved": "Liitumistaotlus kinnitati",
        "company.join_request_rejected": "Liitumistaotlus lükati tagasi",
        "company.join_request_cancelled": "Liitumistaotlus tühistati",
        "company.invitation_accepted": "Ettevõtte kutse võeti vastu",
        "company.invitation_rejected": "Ettevõtte kutse lükati tagasi",
        "company.invitation_revoked": "Ettevõtte kutse võeti tagasi",
        "company.membership_workflow_expired": "Liitumistaotlus või kutse aegus",
        "company.user_contact_email_updated": "Kasutaja ettevõtte kontakt-e-post muudeti",
        "company.user_role_changed": "Kasutaja rollid muudeti",
        "company.contracting_authority_types_changed": "Hankija määratlust muudeti",
        "company.user_removed": "Kasutaja eemaldati ettevõttest",
        "company.user_left": "Kasutaja lahkus ettevõttest",
        "company.relationship_requested": "Ettevõtete seost taotleti",
        "company.relationship_parent_approved": "Emaettevõte kinnitas seose",
        "company.relationship_child_approved": "Tütarettevõte kinnitas seose",
        "company.relationship_confirmed": "Ettevõtete seos kinnitati",
        "company.relationship_rejected": "Ettevõtete seos lükati tagasi",
        "company.relationship_expired": "Ettevõtete seose taotlus aegus",
        "company.relationship_disabled": "Ettevõtete seos lõpetati",
        "company.relationship_policy_change_requested": "Ettevõtete seose õiguste muutmist taotleti",
        "company.membership_created": "Ettevõtte liikmesus loodi",
        "company.membership_roles_changed": "Ettevõtte liikmesuse rolle muudeti",
        "company.membership_suspended": "Ettevõtte liikmesus peatati",
        "company.membership_ended": "Ettevõtte liikmesus lõpetati",
        "company.membership_transfer_requested": "Ettevõtte liikmesuse ülekandmist taotleti",
        "company.membership_transfer_approved": "Ettevõtte liikmesuse ülekanne kinnitati",
        "company.membership_transfer_rejected": "Ettevõtte liikmesuse ülekanne lükati tagasi",
        "company.membership_transfer_completed": "Ettevõtte liikmesuse ülekanne viidi lõpule",
        "company.conflict_detected": "Ettevõtte ligipääsukonflikt tuvastati",
        "company.conflict_recusal_recorded": "Ligipääsukonflikti tõttu taandumine registreeriti",
        "cpv_summation.report_red": "CPV summeerimise kontroll andis punase tulemuse",
        "cpv_summation.approval_requested": "CPV summeerimise kinnitamist taotleti",
        "cpv_summation.approved": "CPV summeerimine kinnitati",
        "procurement.created": "Hanke ettevalmistamist alustati",
        "procurement.modified": "Ettevalmistamisel hanget muudeti",
        "procurement.published": "Hange avaldati",
        "procurement.reconciliation_invitation_sent": "Kutse täpsustavasse vooru",
        "procurement.amended": "Hanget muudeti",
        "procurement.opening_protocol_shared": "Pakkumuste avamise protokoll tehti pakkujatele nähtavaks",
        "procurement.amendment_notification_sent": "Hanke muudatuse teavitus saadeti",
        "procurement.deadline_changed": "Hanke tähtaeg muudeti",
        "procurement.deadline_auto_extended": "Hanke tähtaeg pikenes automaatselt",
        "procurement.closed_without_contract": "Lõpetamine ilma lepinguta",
        "procurement.draft_cancelled": "Hanke koostamine tühistati",
        "procurement.deleted": "Hange kustutati",
        "procurement.tenderer_registered": "Pakkuja registreerus hankele",
        "procurement.tenderer_registration_withdrawn": "Pakkuja tühistas hankele registreerimise",
        "procurement.status_changed": "Hanke staatus muudeti",
        "procurement.deadline_passed": "Hanke tähtaeg möödas",
        "procurement.team_member_added": "Kasutaja lisati hanke meeskonda",
        "procurement.team_member_role_changed": "Hanke meeskonnaliikme roll muudeti",
        "procurement.team_updated": "Hanke meeskonda muudeti",
        "procurement.tenderer_team_updated": "Pakkuja meeskonda muudeti",
        "procurement.responsible_changed": "Hanke vastutav isik muudeti",
        "procurement.self_added_to_team": "Kasutaja lisas end hanke meeskonda",
        "procurement.conflict_confirmed": "Hanke huvide konflikti puudumine kinnitati",
        "procurement.consultation_created": "Hanke konsultatsioonitaotlus loodi",
        "procurement.consultation_commented": "Hanke konsultatsioonile lisati kommentaar",
        "offer.draft_created": "Pakkumuse koostamist alustati",
        "offer.draft_modified": "Ettevalmistamisel pakkumust muudeti",
        "offer.submitted": "Pakkumus esitati",
        "offer.part_modified": "Pakkumust muudeti",
        "offer.part_rescinded": "Hankeosa pakkumus võeti tagasi",
        "offer.part_closed_without_contract": "Hankeosa lõpetati ilma lepinguta",
        "offer.opened": "Pakkumused avati",
        "offer.sealed_data_decryption_authorized": "Pakkumuse suletud andmed dekrüpteeriti",
        "offer.sealed_data_decryption_denied": "Pakkumuse suletud andmete dekrüpteerimisest keelduti",
        "offer.required_documents_changed": "Pakkumus kaotas kehtivuse dokumendinõuete muutumise tõttu",
        "offer.rescinded": "Pakkumus võeti tagasi",
        "offer.amendment_confirmed": "Pakkumus kinnitati pärast hanke muudatust",
        "offer.amendment_modified": "Pakkumust muudeti pärast hanke muudatust",
        "offer.amendment_withdrawn": "Pakkumus võeti pärast hanke muudatust tagasi",
        "offer.amendment_objected": "Pakkuja vaidlustas hanke muudatuse",
        "offer.team_updated": "Pakkuja meeskonda muudeti",
        "offer.responsible_changed": "Pakkumuse vastutav isik muudeti",
        "offer.self_added_to_team": "Kasutaja lisas end pakkuja meeskonda",
        "offer.evaluation_started": "Pakkumuste hindamist alustati",
        "offer.part_evaluated": "Pakkumuse osa hinnati",
        "offer.winner_selected": "Pakkumus tunnistati edukaks",
        "offer.evaluation_finalized": "Hindamine lõpetati",
        "offer.evaluation_reopened": "Varasem otsus muudeti ja hindamine jätkub",
        "offer.notification_sent": "Pakkumuse tulemus saadeti",
        "contract.created": "Leping loodi",
        "contract.signed": "Leping allkirjastati",
        "contract.cancelled": "Lepingut ei sõlmitud",
        "contract.details_updated": "Lepingu andmeid muudeti",
        "contract.completed": "Leping märgiti lõpetatuks",
        "file.uploaded": "Fail laaditi üles",
        "file.deleted": "Fail kustutati",
        "file.accessed": "Fail avati",
        "file.restored": "Fail taastati",
        "question.asked": "Küsimus esitati",
        "question.answered": "Küsimusele vastati",
        "procurerBroadcast.sent": "Hankija saatis sõnumi",
        "privateMessage.sent": "Privaatne sõnum saadeti",
        "question.deleted": "Küsimus kustutati",
        "communication.email_delivered": "E-kiri edastati",
        "communication.email_delivery_failed": "E-kirja edastamine ebaõnnestus lõplikult",
        "admin.action_performed": "Administraatori toiming tehti",
        "admin.settings_changed": "Seadistust muudeti"
    } as Record<string, string>

}


export const RHNewProcurementPreviewViewTokens = {

    actionButtonRowButtonSlots: 3,
    footerClearanceTopGap: RHSpacing.large,
    // Matches the editor view's clearance below its final action row so the
    // fixed application footer never covers the end of a tall preview.
    footerClearanceHeight: 112

}


export const RHProcurementPartEditorViewTokens = {

    fieldLabelFontWeight: RHFont.weightSemibold,
    removePartButtonWidth: 160

}


export const RHInvitedCompaniesViewTokens = {

    multilineHeaderLineHeight: RHFont.lineHeightHeading,  // multiline header rhythm; old: "1.15" — clipped g/j descenders
    multilineHeaderContentPadding: 2,   // sortable-button inner padding (room for the sort caret)

    // Column widths live on the descriptors (weight/absoluteWidth) in
    // InvitedCompaniesView._sharedCompanyDescriptors, per webclient/CLAUDE.md.

    dialogTableHeaderHeight: 66,
    previewTableHeaderHeight: 88,
    companiesTableHeight: 700,

    manualImportButtonWidth: 160,
    manualImportButtonHeight: RHButtons.compactHeight + 4,
    manualImportButtonVariant: RHButtons.compactPrimaryVariant,

    dialogConfirmButtonVariant: "success" as const,
    dialogMaxContentWidth: 1320
    // old: 1100

}


export const RHRequiredDocumentsEditorViewTokens = {

    removeButtonSize: RHButtons.dialogCloseSize - 4

}


export const RHCostSummaryViewTokens = {

    breakdownTopGap: RHSpacing.tiny * 0.5

}


export const RHQAViewTokens = {

    headerRowHeight: 32,
    priorRoundDividerHeight: 1,
    badgeHeight: 24,
    badgeFontWeight: RHFont.weightBold,
    badgeBorderRadius: `${RHRadius.large}px`,
    badgePadding: 6

}


export const RHCompanyQuestionsCardViewTokens = {

    headerHeight: 40,
    headerHorizontalPadding: 12,
    companyNameFontWeight: RHFont.weightSemibold,
    companyNameLetterSpacing: "0.01em",
    lastUpdateFontWeight: RHFont.weightMedium,  // slightly denser timestamp for readability
    mutedOpacity: 0.55  // a prior round's carried-over card, reading as archived rather than actionable

}


export const RHQuestionAnswerPairViewTokens = {

    metadataRowHeight: 18,
    messageTextFontWeight: RHFont.weightRegular,
    questionBubbleTrailingInset: 20,
    unansweredQuestionBubbleTrailingInset: 20,
    answerBubbleTrailingInset: 0,
    answerSectionLeadingInset: 20

}


export const RHOfferMessageThreadViewTokens = {

    bubbleContentInset: 12,
    bubbleMetadataRowHeight: 18

}


export const RHPrivateMessageThreadViewTokens = {

    conversationHeaderHeight: 48,
    messageMinimumWidth: 260,
    messageMaximumWidthRatio: 0.72,
    offererThreadMaxWidth: 1100,
    composerMinimumHeight: 44,
    composerMaximumHeight: 144,
    composerSendButtonWidth: 96,
    threadListWidth: 280,
    threadViewHeight: 760,
    threadViewMinimumHeight: 480,
    threadListRowHeight: 56,
    threadListRowGap: RHSpacing.medium,
    threadListRowBorderWidth: 0.75,
    threadListSelectedRowBorderWidth: 1,
    threadListRowHorizontalPadding: 12,
    threadListRowTextHeight: 18

}
